import type { QueueItemDetail } from '@rp/contracts'
import { useEffect, useState } from 'react'
import { useReadySession } from '../../session/useSession'
import { readDraft, sessionDraftStorage, type DraftScope } from './draftStorage'
import { restoredSend, type RestoredSend } from './sendState'
import { usePersistedDraft } from './usePersistedDraft'
import { useSendReply } from './useSendReply'

export type AcceptedSuggestion = { id: string; text: string }

type InitialDraft = RestoredSend & { answering: string | null }

const initialDraft = (item: QueueItemDetail, scope: DraftScope): InitialDraft => {
  const latest = item.latestInboundMessageId
  const restored = restoredSend(item.replies, latest, scope.userId)
  const fresh = { ...restored, answering: latest }
  if (restored.state.kind !== 'idle') return fresh
  const stored = readDraft(sessionDraftStorage(), scope)
  if (stored === null) return fresh
  const answersLatest = stored.inReplyToMessageId === latest
  return {
    ...restored,
    text: stored.text,
    suggestionId: answersLatest ? stored.suggestionId : null,
    answering: stored.inReplyToMessageId,
  }
}

export const useReplyDraft = (item: QueueItemDetail) => {
  const { me, membership } = useReadySession()
  const scope: DraftScope = { userId: me.user.id, tenantId: membership.tenantId, itemId: item.id }
  const [initial] = useState(() => initialDraft(item, scope))
  const [text, setText] = useState(initial.text)
  const [acceptedSuggestionId, setAcceptedSuggestionId] = useState<string | null>(initial.suggestionId)
  const [answering, setAnswering] = useState(initial.answering)
  const { send, pending, state, reset } = useSendReply(item, initial.state)
  const sent = state.kind === 'sent'
  const accepted = sent || state.kind === 'publishing'
  usePersistedDraft({
    scope,
    text: accepted ? '' : text,
    suggestionId: acceptedSuggestionId,
    inReplyToMessageId: answering,
  })

  useEffect(() => {
    if (!sent) return
    setText('')
    setAcceptedSuggestionId(null)
  }, [sent])

  const edit = (value: string) => {
    setText(value)
    setAnswering(item.latestInboundMessageId)
    if (value.length === 0) setAcceptedSuggestionId(null)
    reset()
  }

  const acceptSuggestion = (suggestion: AcceptedSuggestion) => {
    edit(suggestion.text)
    setAcceptedSuggestionId(suggestion.id)
  }

  const adoptSource = (sourceText: string) => {
    edit(sourceText)
    setAcceptedSuggestionId(null)
  }

  const staleDraft = text.length > 0 && answering !== item.latestInboundMessageId

  return { text, acceptedSuggestionId, staleDraft, edit, acceptSuggestion, adoptSource, send, pending, state }
}

export type ReplyDraft = ReturnType<typeof useReplyDraft>
